import React from "react";
import { cn } from "@/lib/utils";
import { VersionDetailsProps } from "./types";
import { VersionActionButtons } from "./VersionActionButtons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  X,
  Clock,
  User,
  Tag,
  GitCommit,
  FileText,
  Calendar,
  Info,
  Activity,
  CheckCircle,
  Circle,
  Edit,
  Plus,
  Minus,
  Move,
  AlertCircle,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

const getChangeIcon = (type: string) => {
  switch (type) {
    case "create":
      return <Plus className="w-3 h-3 text-green-500" />;
    case "update":
      return <Edit className="w-3 h-3 text-blue-500" />;
    case "delete":
      return <Minus className="w-3 h-3 text-red-500" />;
    case "move":
      return <Move className="w-3 h-3 text-purple-500" />;
    default:
      return <Circle className="w-3 h-3 text-muted-foreground" />;
  }
};

const getChangeTypeLabel = (type: string): string => {
  switch (type) {
    case "create":
      return "Created";
    case "update":
      return "Updated";
    case "delete":
      return "Deleted";
    case "move":
      return "Moved";
    default:
      return type;
  }
};

const getTargetTypeLabel = (target: string): string => {
  switch (target) {
    case "component":
      return "Component";
    case "page":
      return "Page";
    case "navigation":
      return "Navigation";
    case "settings":
      return "Settings";
    default:
      return target;
  }
};

const getUserInitials = (name: string): string => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const VersionDetails: React.FC<VersionDetailsProps> = ({
  version,
  onClose,
  onRestore,
  onCompare,
  onDelete,
  onPublish,
}) => {
  const userInitials = getUserInitials(version.createdBy);
  const timeAgo = formatDistanceToNow(version.createdAt, { addSuffix: true });
  const formattedDate = format(version.createdAt, "PPpp");

  const isPublished = version.isPublished;
  const changeCount = version.changes?.length || 0;

  // Group changes by type and target
  const groupedChanges =
    version.changes?.reduce(
      (groups, change) => {
        const key = `${change.target}-${change.type}`;
        if (!groups[key]) {
          groups[key] = {
            type: change.type,
            target: change.target,
            changes: [],
          };
        }
        groups[key].changes.push(change);
        return groups;
      },
      {} as Record<
        string,
        { type: string; target: string; changes: typeof version.changes }
      >,
    ) || {};

  const changeSummary = Object.values(groupedChanges).map((group) => ({
    ...group,
    count: group.changes.length,
  }));

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className={cn(
                  "w-4 h-4 rounded-full",
                  isPublished ? "bg-blue-500" : "bg-orange-500",
                )}
              />
              <DialogTitle className="text-xl font-bold">
                Version {version.version}
              </DialogTitle>
              {version.name && (
                <span className="text-lg font-medium text-muted-foreground">
                  {version.name}
                </span>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Badge variant={isPublished ? "default" : "outline"}>
                {isPublished ? "Published" : "Draft"}
              </Badge>

              <DialogClose asChild>
                <Button variant="outline" size="sm">
                  <X className="w-4 h-4" />
                </Button>
              </DialogClose>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="space-y-6 pb-6">
            {/* Version Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-base">
                  <Info className="w-4 h-4" />
                  <span>Version Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Description */}
                {version.description && (
                  <div>
                    <h4 className="font-medium text-sm mb-2">Description</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {version.description}
                    </p>
                  </div>
                )}

                {/* Metadata Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Author */}
                  <div className="flex items-center space-x-3">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="text-sm">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{version.createdBy}</p>
                      <p className="text-xs text-muted-foreground">Author</p>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{timeAgo}</p>
                      <p className="text-xs text-muted-foreground">
                        {formattedDate}
                      </p>
                    </div>
                  </div>

                  {/* Version Number */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <Activity className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm font-mono">
                        v{version.version}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Version Number
                      </p>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      {isPublished ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Circle className="w-4 h-4 text-orange-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {isPublished ? "Published" : "Draft"}
                      </p>
                      <p className="text-xs text-muted-foreground">Status</p>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {version.tags && version.tags.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2 flex items-center space-x-2">
                      <Tag className="w-4 h-4" />
                      <span>Tags</span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {version.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Changes Card */}
            {changeCount > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-base">
                    <GitCommit className="w-4 h-4" />
                    <span>Changes ({changeCount})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Change Summary */}
                  {changeSummary.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-sm mb-3">Summary</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {changeSummary.map(({ type, target, count }) => (
                          <div
                            key={`${type}-${target}`}
                            className="flex items-center space-x-2 p-2 rounded-md bg-muted/50"
                          >
                            {getChangeIcon(type)}
                            <span className="text-xs">
                              {count} {getTargetTypeLabel(target)}
                              {count !== 1 ? "s" : ""}{" "}
                              {getChangeTypeLabel(type).toLowerCase()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Separator className="my-4" />

                  {/* Detailed Changes */}
                  <div>
                    <h4 className="font-medium text-sm mb-3">Details</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {version.changes?.map((change, index) => (
                        <div
                          key={change.id || index}
                          className="flex items-start space-x-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {getChangeIcon(change.type)}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium capitalize">
                                {getChangeTypeLabel(change.type)}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {getTargetTypeLabel(change.target)}
                              </Badge>
                              {change.field && (
                                <span className="text-xs text-muted-foreground">
                                  • {change.field}
                                </span>
                              )}
                            </div>

                            {change.description && (
                              <p className="text-sm text-muted-foreground mb-1">
                                {change.description}
                              </p>
                            )}

                            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              <span>
                                {format(change.timestamp, "HH:mm:ss")}
                              </span>
                              {change.userId && (
                                <>
                                  <span>•</span>
                                  <span>{change.userId}</span>
                                </>
                              )}
                            </div>

                            {/* Show old/new values for updates */}
                            {change.type === "update" &&
                              (change.oldValue !== undefined ||
                                change.newValue !== undefined) && (
                                <div className="mt-2 p-2 bg-muted/30 rounded text-xs">
                                  {change.oldValue !== undefined && (
                                    <div className="text-red-600 dark:text-red-400">
                                      - {JSON.stringify(change.oldValue)}
                                    </div>
                                  )}
                                  {change.newValue !== undefined && (
                                    <div className="text-green-600 dark:text-green-400">
                                      + {JSON.stringify(change.newValue)}
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Changes Message */}
            {changeCount === 0 && (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No changes recorded for this version
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        {/* Actions Footer */}
        <div className="p-6 pt-0 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span>Version {version.version} details</span>
            </div>

            <VersionActionButtons
              version={version}
              isCurrent={false} // Details modal doesn't show current version actions
              onRestore={onRestore}
              onCompare={onCompare}
              onDelete={onDelete}
              onPublish={onPublish}
              size="sm"
              variant="inline"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VersionDetails;
